// @ts-strict-ignore
import {Component, computed, effect, inject, OnInit, signal} from "@angular/core";
import {Translations} from "@translations";
import {
	SimConfigurationService,
	SimMaterialDialogService,
	SimSessionService,
	SimUtilityService,
	TrackByMethods,
} from "@services";
import {TeamTrainingSession_Step2Form, TeamTrainingSessionStateService} from "../../service/team-training-session-state.service";
import {
	AddRunToTeamTrainingSessionRequest,
	ApiError,
	ExternalAvailabilityResponse,
	FetchDataType,
	Scenario,
	ScenarioRecordingGroup,
	SchedulingConflict,
	TranslationKey,
	UpdateRunInTeamTrainingSessionRequest,
} from "@models";
import {FormBuilder, FormControl, Validators} from "@angular/forms";
import {Subscription} from "rxjs";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {debounceTime, distinctUntilChanged, filter} from "rxjs/operators";
import {SimTimeService} from "../../../../../../../core/services/sim-services/sim-time.service";
import {TranslateService} from "@ngx-translate/core";
import dayjs from "dayjs";

@UntilDestroy()
@Component({
	selector: "sim-team-training-session-step2",
	templateUrl: "./team-training-session-step2.component.html",
	styleUrl: "./team-training-session-step2.component.scss",
	standalone: false,
})
export class TeamTrainingSessionStep2Component implements OnInit {
	// Signals
	public currentRunIndex = signal<number>(0);
	public isScenarioLoaded = signal<boolean>(true);
	public displayRuns = signal<ScenarioRecordingGroup[]>([]);
	public currentForm = computed<TeamTrainingSession_Step2Form>(() => this._computeCurrentForm());
	public disableAddRun = computed<boolean>(() => this.displayRuns()?.some((run) => !run.scenarioRecordingGroupId));

	// Readonly
	protected readonly T = Translations;
	protected readonly TrackByMethods = TrackByMethods;
	protected readonly FetchDataType = FetchDataType;

	// Injections
	public stateService = inject(TeamTrainingSessionStateService);
	private _sessionService = inject(SimSessionService);
	private readonly _dialogService = inject(SimMaterialDialogService);
	protected readonly _translate = inject(TranslateService);

	// Form
	public isReady: boolean = true;
	public step2Form = this.stateService.step2Form;
	private _formBuilder = new FormBuilder();
	private _subscription: Subscription | null = null;

	constructor() {
		effect(() => this._effectObserveFormChanges(), {allowSignalWrites: true});
		effect(() => this._effectValidateCurrentIndex(), {allowSignalWrites: true});
	}

	public get emptyScenarioGroup(): TeamTrainingSession_Step2Form {
		return this._formBuilder.group({
			scenario: new FormControl<Scenario>(null, [Validators.required]),
			start: new FormControl<string>(null, [Validators.required]),
			end: new FormControl<string>(null, [Validators.required]),
		}) as TeamTrainingSession_Step2Form;
	}

	public ngOnInit() {
		this.displayRuns.set(this.stateService.session.scenarioRecordingGroups);
		if (!this.step2Form.value.length) {
			this.addRun();
		}
	}

	public addRun(): void {
		this.displayRuns.update((previousRuns) =>
			previousRuns?.length ? [...previousRuns, {} as ScenarioRecordingGroup] : [{} as ScenarioRecordingGroup],
		);
		this.step2Form.push(this.emptyScenarioGroup);
		this.currentRunIndex.set(this.displayRuns().length - 1);
	}

	public removeRun(runId: string, index: number): void {
		// Pre-existing Run
		if (runId) {
			this._sessionService
				.removeRunFromTeamTrainingSession({scenarioRecordingGroupId: runId, teamTrainingSessionId: this.stateService.session.id})
				.then((session) => (this.stateService.session = session));
			this.displayRuns.update((previousRuns) => previousRuns.filter((run) => run.scenarioRecordingGroupId !== runId));
		}
		// New Run (UI only)
		else {
			this.displayRuns.update((previousStations) => previousStations.filter((run) => !!run.scenarioRecordingGroupId));
		}
		// Form Control
		this.step2Form.removeAt(index);
		if (this.currentRunIndex() === this.displayRuns().length) {
			this.currentRunIndex.set(this.displayRuns().length - 1);
		}
	}

	public validateEndTimeBound(): boolean {
		if (!this.currentForm().value?.end || !this.currentForm().value?.start) {
			return;
		}
		const endTime = SimTimeService.addTimeStringToDate(new Date(), this.currentForm().value.end);
		const startTime = SimTimeService.addTimeStringToDate(new Date(), this.currentForm().value.start);
		if (!endTime || !startTime) {
			return true;
		}
		if (endTime < startTime) {
			this.currentForm().patchValue({end: null});
			this.currentForm().controls.end.setErrors({invalid: true});
			this.stateService.autosaveWarning = this.T.ACTIVE_EVENT_TXT as TranslationKey;
			return false;
		}
		return true;
	}

	protected getEndTimeLimit(): string | null {
		if (this.currentForm().value.start) {
			const result = SimTimeService.addTimeStringToDate(new Date(), this.currentForm().value.start);
			return result.toISOString();
		}
	}

	private _effectObserveFormChanges() {
		// Validate
		if (!this.currentForm()) {
			return;
		}

		// Cooldown to prevent station cloning
		this.isReady = false;
		const isLoad = !!this.stateService.session.scenarioRecordingGroups.at(this.currentRunIndex())?.scenarioRecordingGroupId;
		this.isScenarioLoaded.set(!isLoad);
		setTimeout(() => (this.isReady = true), 50);

		// Unsubscribe from previous
		if (this._subscription) {
			this._subscription.unsubscribe();
		}

		// Subscribe to changes
		this._subscription = this.currentForm()
			.valueChanges.pipe(
				untilDestroyed(this),
				distinctUntilChanged(),
				filter(Boolean),
				filter(() => this.currentForm().valid),
				debounceTime(500),
			)
			.subscribe((value) => {
				if (this.validateEndTimeBound()) {
					this._upsertUserInput(value);
				}
			});
	}

	private _effectValidateCurrentIndex() {
		const currentIndex = this.currentRunIndex();
		if (currentIndex === null || !this.currentForm() || currentIndex >= this.displayRuns().length) {
			this.currentRunIndex.set(this.displayRuns().length - 1);
		}
	}

	private async _upsertUserInput(value: TeamTrainingSession_Step2Form["value"]) {
		const date = new Date(this.stateService.session.startTime);

		const plannedStartTime = SimTimeService.addTimeStringToDate(date, value.start);
		const plannedEndTime = SimTimeService.addTimeStringToDate(date, value.end);

		let canSave = true;

		if (SimConfigurationService.settings.externalEventIntegrationActive) {
			this.stateService.autosaveWarning = null;
			const checkPromise = this._sessionService.getExternalAvailability(
				this.stateService.session.id,
				plannedStartTime,
				plannedEndTime,
			);
			this.stateService.autosavePromise = checkPromise;
			await checkPromise
				.then((result) => {
					if (result != null) {
						if (result.conflicts.length > 0) {
							this.updateValidationToSchedulingConflicts(result, plannedStartTime, plannedEndTime);

							this.stateService.autosaveWarning = this.T.TEAM_TRAINING_SESSION_DIALOG_STEP2_SESSION_EXTERNAL_INVALID;
							canSave = false;

							let listOfConflictMessages = result.conflicts.map((conflict) => SimUtilityService.getFormattedSchedulingConflictMessage(conflict));

							this._dialogService.openListViewDialog({
								okText: this._translate.instant(this.T.BUTTON_OK),
								title: this._translate.instant(this.T.LABEL_WARNING),
								description: this._translate.instant(this.T.TEAM_TRAINING_SESSION_DIALOG_STEP2_SESSION_EXTERNAL_INVALID),
								myList: listOfConflictMessages,
							});
						} else {
							this.currentForm().controls.start.setErrors(null);
							this.currentForm().controls.end.setErrors(null);
							this.stateService.autosaveWarning = null;
							canSave = true;
						}
					}
				})
				.catch(async (error: ApiError) => {
					canSave = false;
					let warningText = error?.simError?.message;
					if (error?.simError?.translationKey) {
						warningText = this._translate.instant(error.simError.translationKey);
					}
					this._dialogService.openGeneralWarningDialog({
						warningOkText: this._translate.instant(this.T.BUTTON_OK),
						warningDescription: warningText,
						warningTitle: this._translate.instant(this.T.LABEL_WARNING),
						showOnlyConfirmation: true,
					});
				});
		}

		if (!canSave) {
			return;
		}
		const request: AddRunToTeamTrainingSessionRequest = {
			scenarioId: value.scenario.id,
			teamTrainingSessionId: this.stateService.session.id,
			plannedEnd: plannedEndTime,
			plannedStart: plannedStartTime,
		};
		const runId = this.stateService.session.scenarioRecordingGroups.at(this.currentRunIndex())?.scenarioRecordingGroupId;
		const savePromise = runId
			? this._sessionService.updateRunInTeamTrainingSession({
					scenarioRecordingGroupId: runId,
					...request,
				} as UpdateRunInTeamTrainingSessionRequest)
			: this._sessionService.addRunToTeamTrainingSession(request);
		this.stateService.autosavePromise = savePromise;
		savePromise.then((session) => {
			this.stateService.session = session;
			this.displayRuns.set(session.scenarioRecordingGroups);
		});
	}

	private updateValidationToSchedulingConflicts(result: ExternalAvailabilityResponse, plannedStartTime: Date, plannedEndTime: Date) {
		let firstConflictDate = new Date(result.firstConflictTime);
		let lastConflictDate = new Date(result.lastConflictTime);
		let hasStartError = plannedStartTime > firstConflictDate;
		let hasEndError = plannedEndTime < lastConflictDate;

		//When all conflicts are inside the planned time range, the whole range is invalidated
		if (plannedStartTime < firstConflictDate && plannedEndTime > lastConflictDate) {
			hasStartError = true;
			hasEndError = true;
		}

		if (hasStartError) {
			this.currentForm().controls.start.setErrors({invalid: true});
		} else {
			this.currentForm().controls.start.setErrors(null);
		}

		if (hasEndError) {
			this.currentForm().controls.end.setErrors({invalid: true});
		} else {
			this.currentForm().controls.end.setErrors(null);
		}
	}

	private _computeCurrentForm(): TeamTrainingSession_Step2Form {
		const result = this.stateService.step2Form.at(this.currentRunIndex());
		result?.markAsPristine();
		return result;
	}
}
